import { useState, useRef, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { ShoppingBag, User, Search, ChevronDown, Menu, X, Instagram, Facebook, Youtube, ArrowRight } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import AskSpotter from "@/components/AskSpotter";
import Concierge from "@/components/Concierge";
import ShopMegaMenu from "@/components/ShopMegaMenu";
import { useConcierge } from "@/context/ConciergeContext";
import { COMPANY } from "@/config/company";
import api from "@/lib/api";

function NewsletterSignup() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) return toast.error("Enter a valid email.");
    setBusy(true);
    try { await api.post("/newsletter/subscribe", { email }); setDone(true); toast.success("You're on the list."); }
    catch { toast.error("Couldn't subscribe — try again."); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-8 max-w-sm">
      <p className="font-mono text-[10px] uppercase tracking-widest text-background/60 mb-2">Stay in the loop</p>
      {done ? (
        <p className="text-sm text-background/70">Thanks — new finds and guides are on the way.</p>
      ) : (
        <form onSubmit={submit} className="flex border border-background/30">
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your email"
            className="flex-1 bg-transparent px-3 py-2.5 text-sm placeholder:text-background/40 focus:outline-none" />
          <button disabled={busy} aria-label="Subscribe"
            className="px-4 bg-background text-foreground hover:bg-[hsl(var(--accent))] hover:text-background disabled:opacity-50">
            <ArrowRight size={16} />
          </button>
        </form>
      )}
    </div>
  );
}

const XIcon = (p) => (
  <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor" {...p}>
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24h-6.66l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231 5.45-6.231Zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77Z" />
  </svg>
);
const PinterestIcon = (p) => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" {...p}>
    <path d="M12 0C5.373 0 0 5.372 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.405-5.965 1.405-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.03-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.137.893 2.739a.36.36 0 0 1 .083.345c-.091.378-.293 1.194-.333 1.361-.052.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z" />
  </svg>
);

function SocialLinks() {
  const s = COMPANY.social || {};
  const items = [
    { url: s.instagram, Icon: Instagram, label: "Instagram" },
    { url: s.facebook, Icon: Facebook, label: "Facebook" },
    { url: s.youtube, Icon: Youtube, label: "YouTube" },
    { url: s.pinterest, Icon: PinterestIcon, label: "Pinterest" },
    { url: s.x, Icon: XIcon, label: "X" },
  ].filter((i) => i.url);
  if (!items.length) return null;
  return (
    <div className="mt-6 flex gap-3">
      {items.map(({ url, Icon, label }) => (
        <a key={label} href={url} target="_blank" rel="noopener noreferrer" aria-label={label}
          className="w-9 h-9 border border-background/30 flex items-center justify-center hover:bg-[hsl(var(--accent))] hover:border-[hsl(var(--accent))] transition-colors">
          <Icon size={16} />
        </a>
      ))}
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const { openConcierge, hasProfile } = useConcierge();
  const [shopNav, setShopNav] = useState([]);
  const [shopOpen, setShopOpen] = useState(false);
  const shopTimer = useRef(null);

  useEffect(() => { api.get("/nav/shop").then((r) => setShopNav(r.data || [])).catch(() => {}); }, []);
  const openShop = () => { clearTimeout(shopTimer.current); setShopOpen(true); };
  const closeShop = () => { shopTimer.current = setTimeout(() => setShopOpen(false), 140); };
  const nav = useNavigate();
  const loc = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileDept, setMobileDept] = useState(null);

  // Search overlay. The input stays mounted (opacity-toggled, never display:none)
  // so we can focus it synchronously inside the click handler — that's what makes
  // the on-screen keyboard appear on mobile Safari/Chrome.
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const searchInputRef = useRef(null);
  const openSearch = () => { setSearchOpen(true); searchInputRef.current?.focus(); };
  const closeSearch = () => { setSearchOpen(false); searchInputRef.current?.blur(); };
  const submitSearch = (e) => {
    e.preventDefault();
    const query = searchQ.trim();
    setSearchOpen(false);
    nav(query ? `/shop?q=${encodeURIComponent(query)}` : "/shop");
  };
  useEffect(() => {
    if (!searchOpen) return;
    const onKey = (e) => { if (e.key === "Escape") closeSearch(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [searchOpen]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const dashLink = () => {
    if (!user) return "/login";
    if (user.role === "admin") return "/admin";
    if (user.role === "vendor") return "/vendor";
    return "/account";
  };

  const accountLinks = (user?.role === "buyer"
    ? [
        { to: "/account/profile", label: "My Account" },
        { to: "/account", label: "My Orders" },
        { to: "/account/addresses", label: "Saved Addresses" },
        { to: "/account/wishlist", label: "Wishlist" },
        { to: "/account/credit", label: "Store Credit" },
      ]
    : [{ to: dashLink(), label: "Dashboard" }]
  ).concat([{ to: "/account/security", label: "Change Password" }]);

  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [menuOpen]);

  useEffect(() => { setMenuOpen(false); setShopOpen(false); setMobileOpen(false); }, [loc.pathname]);

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      {/* Ticker */}
      <div className="bg-foreground text-background overflow-hidden">
        <div className="flex animate-marquee whitespace-nowrap py-2 font-mono text-[11px] tracking-widest uppercase">
          {Array.from({ length: 2 }).map((_, k) => (
            <div key={k} className="flex shrink-0">
              {[
                "SPOTTER CERTIFIED",
                "BIS SIZE MATCH LIVE",
                "FREE SHIPPING",
                "GST INCLUDED",
                "MADE IN INDIA",
                "SHIPPED WORLDWIDE",
              ].map((t, i) => (
                <span key={i} className="px-8 flex items-center gap-8">
                  {t}
                  <span className="text-[hsl(var(--accent))]">◆</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Header — sticky: the ticker scrolls away, the header freezes to the top */}
      <header className="sticky top-0 z-40 border-b border-foreground/90 bg-background relative">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 h-20 flex items-center justify-between gap-6">
          <Link to="/" data-testid="brand-logo" className="flex flex-col md:flex-row md:items-baseline md:gap-2 leading-none">
            <span className="font-serif text-2xl md:text-3xl font-black tracking-tighter">TheSpotters</span>
            <span className="font-mono text-[8px] md:text-[10px] uppercase tracking-widest text-muted-foreground mt-1 md:mt-0">
              /Certif. № TS-2026
            </span>
          </Link>
          <nav className="hidden md:flex items-center gap-8 font-mono text-[11px] uppercase tracking-[0.18em]">
            <div onMouseEnter={openShop} onMouseLeave={closeShop} className="py-7 -my-7">
              <Link data-testid="nav-shop" to="/shop" className={`hover:text-[hsl(var(--accent))] ${shopOpen ? "text-[hsl(var(--accent))]" : ""}`}>Shop</Link>
            </div>
            <Link data-testid="nav-process" to="/process" className="hover:text-[hsl(var(--accent))]">Process</Link>
            <Link data-testid="nav-spotters" to="/spotters" className="hover:text-[hsl(var(--accent))]">Spotters</Link>
            <Link data-testid="nav-journal" to="/journal" className="hover:text-[hsl(var(--accent))]">Journal</Link>
          </nav>
          <div className="flex items-center gap-5">
            <button data-testid="my-picks-btn" onClick={() => openConcierge()} aria-label="My Picks"
              className="hidden sm:inline-flex items-center gap-1.5 border border-foreground px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.16em] hover:bg-foreground hover:text-background transition-colors">
              {hasProfile && <span className="w-1.5 h-1.5 bg-[hsl(var(--accent))]" />}
              My Picks
            </button>
            <button data-testid="search-btn" onClick={openSearch} aria-label="search" className="hover:text-[hsl(var(--accent))]">
              <Search size={18} strokeWidth={1.5} />
            </button>
            <Link data-testid="nav-cart" to="/cart" className="relative hover:text-[hsl(var(--accent))]">
              <ShoppingBag size={18} strokeWidth={1.5} />
              {count > 0 && (
                <span className="absolute -top-2 -right-3 bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] text-[10px] px-1.5 font-mono font-bold">
                  {count}
                </span>
              )}
            </Link>
            {user ? (
              <div className="relative" ref={menuRef}>
                <button data-testid="account-menu-btn" onClick={() => setMenuOpen((o) => !o)}
                  className="font-mono text-[11px] uppercase tracking-widest hover:text-[hsl(var(--accent))] flex items-center gap-1.5">
                  <User size={16} strokeWidth={1.5} />
                  {user.name || user.role}
                  <ChevronDown size={13} strokeWidth={1.5} className={`transition-transform ${menuOpen ? "rotate-180" : ""}`} />
                </button>
                {menuOpen && (
                  <div data-testid="account-menu" className="absolute right-0 mt-3 w-60 bg-background border border-foreground z-50">
                    <div className="px-4 py-3 border-b border-foreground/20">
                      <p className="font-serif text-lg tracking-tight leading-none">{user.name || "Account"}</p>
                      {user.phone && <p className="font-mono text-[10px] text-muted-foreground mt-1">{user.phone}</p>}
                      <p className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground mt-1">{user.role}</p>
                    </div>
                    <div className="py-1">
                      {accountLinks.map((mi) => (
                        <Link key={mi.to} to={mi.to}
                          className="block px-4 py-2 font-mono text-[11px] uppercase tracking-widest hover:bg-foreground hover:text-background">
                          {mi.label}
                        </Link>
                      ))}
                    </div>
                    <div className="border-t border-foreground/20 py-1">
                      <Link to="/help"
                        className="block px-4 py-2 font-mono text-[11px] uppercase tracking-widest hover:bg-foreground hover:text-background">
                        Help &amp; Support
                      </Link>
                    </div>
                    <div className="border-t border-foreground/20 py-1">
                      <button data-testid="logout-btn" onClick={() => { setMenuOpen(false); logout(); nav("/"); }}
                        className="w-full text-left px-4 py-2 font-mono text-[11px] uppercase tracking-widest text-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))] hover:text-[hsl(var(--accent-foreground))]">
                        Log Out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <Link data-testid="nav-login" to="/login" className="hidden md:inline font-mono text-[11px] uppercase tracking-widest hover:text-[hsl(var(--accent))]">
                Log in
              </Link>
            )}
            <button data-testid="mobile-menu-btn" onClick={() => setMobileOpen(true)} aria-label="Menu"
              className="md:hidden hover:text-[hsl(var(--accent))]">
              <Menu size={22} strokeWidth={1.5} />
            </button>
          </div>
        </div>
        {shopOpen && shopNav.length > 0 && (
          <ShopMegaMenu nav={shopNav} onEnter={openShop} onLeave={closeShop} onNavigate={() => setShopOpen(false)} />
        )}
      </header>

      {/* Search overlay — always mounted so the input can be focused inside the
          tap (mobile keyboard). Toggled with opacity, never display:none. */}
      <div className={`fixed inset-0 z-[60] transition-opacity duration-200 ${searchOpen ? "opacity-100" : "opacity-0 pointer-events-none"}`}>
        <div className="absolute inset-0 bg-foreground/40 backdrop-blur-sm" onClick={closeSearch} />
        <div className="relative bg-background border-b border-foreground">
          <form onSubmit={submitSearch} className="max-w-[1600px] mx-auto px-6 md:px-12 h-20 flex items-center gap-4">
            <Search size={20} strokeWidth={1.5} className="text-muted-foreground shrink-0" />
            <input
              ref={searchInputRef}
              data-testid="search-overlay-input"
              type="search"
              enterKeyHint="search"
              autoComplete="off"
              value={searchQ}
              onChange={(e) => setSearchQ(e.target.value)}
              placeholder="Search products, brands…"
              className="flex-1 min-w-0 bg-transparent outline-none border-none font-serif text-xl md:text-2xl tracking-tight placeholder:text-muted-foreground/60"
            />
            <button type="button" onClick={closeSearch} aria-label="Close search" className="shrink-0 hover:text-[hsl(var(--accent))]">
              <X size={22} strokeWidth={1.5} />
            </button>
          </form>
        </div>
      </div>

      <main className="flex-1">{children}</main>

      {/* Footer */}
      <footer className="mt-24 border-t border-foreground bg-foreground text-background">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-16 grid md:grid-cols-4 gap-12">
          <div className="md:col-span-2">
            <h3 className="font-serif text-4xl md:text-5xl">Every product. Physically inspected.</h3>
            <p className="mt-6 text-background/70 max-w-md text-sm leading-relaxed">
              Our trio — The Skeptic, The Perfectionist, The Softie — checks every listing against a reference sample for authenticity, quality and fit before it goes live. Approved sellers then ship directly to your door.
            </p>
            <NewsletterSignup />
            <SocialLinks />
          </div>
          <div>
            <p className="font-mono text-[10px] uppercase tracking-widest text-background/60 mb-4">Shop</p>
            <ul className="space-y-2 text-sm">
              <li><Link to="/shop?category=footwear" className="hover:text-[hsl(var(--accent))]">Footwear</Link></li>
              <li><Link to="/shop?category=apparel" className="hover:text-[hsl(var(--accent))]">Apparel</Link></li>
              <li><Link to="/shop?category=sports-equipment" className="hover:text-[hsl(var(--accent))]">Sports Equipment</Link></li>
              <li><Link to="/collections" className="hover:text-[hsl(var(--accent))]">Collections</Link></li>
            </ul>
          </div>
          <div>
            <p className="font-mono text-[10px] uppercase tracking-widest text-background/60 mb-4">The Spotters</p>
            <ul className="space-y-2 text-sm">
              <li><Link to="/process" className="hover:text-[hsl(var(--accent))]">Certification Process</Link></li>
              <li><Link to="/size-match" className="hover:text-[hsl(var(--accent))]">BIS Size Match</Link></li>
              <li><Link to="/vendors" className="hover:text-[hsl(var(--accent))]">All Vendors</Link></li>
              <li><Link to="/become-a-vendor" className="hover:text-[hsl(var(--accent))]">Become a Vendor</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-background/20 py-5 px-6 md:px-12 space-y-3">
          <div className="flex flex-wrap gap-x-5 gap-y-2 font-mono text-[10px] uppercase tracking-widest text-background/60">
            <Link to="/legal/terms" className="hover:text-[hsl(var(--accent))]">Terms of Use</Link>
            <Link to="/legal/privacy" className="hover:text-[hsl(var(--accent))]">Privacy</Link>
            <Link to="/legal/shipping-returns" className="hover:text-[hsl(var(--accent))]">Shipping &amp; Returns</Link>
            <Link to="/legal/grievance" className="hover:text-[hsl(var(--accent))]">Grievance Redressal</Link>
            <Link to="/help" className="hover:text-[hsl(var(--accent))]">Help</Link>
          </div>
          <div className="flex flex-col sm:flex-row justify-between gap-2 font-mono text-[10px] uppercase tracking-widest text-background/60">
            <span>© 2026 {COMPANY.legalName}. All rights reserved.</span>
            <span className="normal-case tracking-normal text-background/45">
              {COMPANY.brand} is owned &amp; operated by {COMPANY.legalName}.
            </span>
            <span>Spotter Certified № TS-2026</span>
          </div>
        </div>
      </footer>
      {/* Mobile slide-in menu */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="absolute inset-0 bg-foreground/40 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <div className="absolute right-0 top-0 h-full w-[86%] max-w-sm bg-background border-l border-foreground flex flex-col animate-[slidein_.25s_ease]">
            <div className="flex items-center justify-between px-5 h-16 border-b border-foreground shrink-0">
              <span className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">Menu</span>
              <button aria-label="Close menu" onClick={() => setMobileOpen(false)} className="hover:text-[hsl(var(--accent))]"><X size={22} strokeWidth={1.5} /></button>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-5">
              {/* Shop + departments */}
              <div className="border-b border-foreground/15 pb-4 mb-4">
                <Link to="/shop" className="block font-serif text-2xl tracking-tight hover:text-[hsl(var(--accent))]">Shop all →</Link>
                {shopNav.map((dep) => (
                  <div key={dep.department} className="mt-2.5">
                    <button onClick={() => setMobileDept((d) => (d === dep.department ? null : dep.department))}
                      className="w-full flex items-center justify-between font-mono text-[11px] uppercase tracking-widest py-1.5">
                      {dep.department}
                      <ChevronDown size={14} strokeWidth={1.5} className={`transition-transform ${mobileDept === dep.department ? "rotate-180" : ""}`} />
                    </button>
                    {mobileDept === dep.department && (
                      <div className="pl-3 pb-2 space-y-1.5 mt-1">
                        <Link to={`/shop?department=${encodeURIComponent(dep.department)}`} className="block text-sm text-muted-foreground hover:text-foreground">All {dep.department}</Link>
                        {dep.categories.map((c) => (
                          <div key={c.id}>
                            <Link to={`/shop?department=${encodeURIComponent(dep.department)}&category_id=${c.id}`} className="block text-sm py-0.5 hover:text-[hsl(var(--accent))]">{c.name}</Link>
                            {c.subcategories?.length > 0 && (
                              <div className="pl-3">
                                {c.subcategories.map((s) => (
                                  <Link key={s.id} to={`/shop?department=${encodeURIComponent(dep.department)}&subcategory_id=${s.id}`} className="block text-[13px] text-muted-foreground hover:text-[hsl(var(--accent))] py-0.5">{s.name}</Link>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {/* Primary nav */}
              <nav className="border-b border-foreground/15 pb-4 mb-4">
                {[{ to: "/process", l: "Process" }, { to: "/spotters", l: "Spotters" }, { to: "/journal", l: "Journal" }].map((x) => (
                  <Link key={x.to} to={x.to} className="block font-serif text-xl py-1.5 hover:text-[hsl(var(--accent))]">{x.l}</Link>
                ))}
              </nav>
              {/* My Picks */}
              <button onClick={() => { setMobileOpen(false); openConcierge(); }}
                className="w-full text-left flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest py-2 border-b border-foreground/15 mb-4">
                {hasProfile && <span className="w-1.5 h-1.5 bg-[hsl(var(--accent))]" />} My Picks →
              </button>
              {/* Account */}
              {user ? (
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1">{user.name || "Account"}</p>
                  {accountLinks.map((mi) => <Link key={mi.to} to={mi.to} className="block text-sm py-1.5 hover:text-[hsl(var(--accent))]">{mi.label}</Link>)}
                  <Link to="/help" className="block text-sm py-1.5 hover:text-[hsl(var(--accent))]">Help &amp; Support</Link>
                  <button onClick={() => { setMobileOpen(false); logout(); nav("/"); }} className="block text-sm py-1.5 text-[hsl(var(--accent))]">Log Out</button>
                </div>
              ) : (
                <div className="flex flex-col gap-2">
                  <Link to="/login" className="bg-foreground text-background text-center py-3 font-mono text-[11px] uppercase tracking-widest hover:bg-[hsl(var(--accent))]">Log in</Link>
                  <Link to="/register" className="border border-foreground text-center py-3 font-mono text-[11px] uppercase tracking-widest hover:bg-foreground hover:text-background">Create account</Link>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <AskSpotter />
      <Concierge />
      <Toaster />
    </div>
  );
}
